import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getUserDetails } from "../../redux/userRelated/userHandle";
import { useNavigate, useParams } from "react-router-dom";
import {
    alpha,
    Avatar,
    Box,
    Button,
    Chip,
    Collapse,
    Container,
    Fade,
    IconButton,
    Paper,
    Skeleton,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import {
    ArrowBack,
    BarChart as BarChartIcon,
    Cake as CakeIcon,
    Contacts as GuardianIcon,
    Email as EmailIcon,
    FactCheck,
    Grading,
    KeyboardArrowDown,
    KeyboardArrowUp,
    Person,
    Phone as PhoneIcon,
    School,
    TableRows,
} from "@mui/icons-material";
import {
    calculateOverallAttendancePercentage,
    calculateSubjectAttendancePercentage,
    groupAttendanceBySubject,
} from "../../components/attendanceCalculator";
import CustomBarChart from "../../components/CustomBarChart";
import CustomPieChart from "../../components/CustomPieChart";
import { PurpleButton } from "../../components/buttonStyles";
import { resolveUploadedMediaUrl } from "../../config/api";
import SpecialNeedsSection from "../../components/student/SpecialNeedsSection";
import { useFormTeacherHomeroom } from "../../hooks/useFormTeacherHomeroom";
import { normalizeEnrollmentStatus } from "../../utils/enrollmentApi";

const enrollmentChipColor = {
    Verified: "success",
    Pending: "warning",
    Rejected: "error",
};

function teachSubjectLabel(currentUser) {
    const ts = currentUser?.teachSubject;
    if (!ts) return null;
    if (typeof ts === "string") return ts;
    return ts.subName || null;
}

const TeacherViewStudent = () => {
    const navigate = useNavigate();
    const params = useParams();
    const dispatch = useDispatch();
    const { currentUser, userDetails, response, loading, error } = useSelector((state) => state.user);
    const { hasHomeroomClass } = useFormTeacherHomeroom();

    const studentID = params.id;
    const mySubject = teachSubjectLabel(currentUser);
    const backTo = hasHomeroomClass ? "/Teacher/my-class/students" : "/Teacher/class";

    const [tab, setTab] = useState("1");
    const [attendanceView, setAttendanceView] = useState("table");
    const [marksView, setMarksView] = useState("table");
    const [openStates, setOpenStates] = useState({});

    useEffect(() => {
        dispatch(getUserDetails(studentID, "Student"));
    }, [dispatch, studentID]);

    const subjectMarks = useMemo(
        () => (Array.isArray(userDetails?.examResult) ? userDetails.examResult : []),
        [userDetails?.examResult],
    );
    const subjectAttendance = useMemo(
        () => (Array.isArray(userDetails?.attendance) ? userDetails.attendance : []),
        [userDetails?.attendance],
    );

    const handleOpen = (subId) => {
        setOpenStates((prev) => ({ ...prev, [subId]: !prev[subId] }));
    };

    const overallAttendancePercentage = calculateOverallAttendancePercentage(subjectAttendance);
    const overallAbsentPercentage = 100 - overallAttendancePercentage;

    const chartData = useMemo(
        () => [
            { name: "Present", value: overallAttendancePercentage },
            { name: "Absent", value: overallAbsentPercentage },
        ],
        [overallAttendancePercentage, overallAbsentPercentage],
    );

    const subjectData = useMemo(
        () =>
            Object.entries(groupAttendanceBySubject(subjectAttendance)).map(
                ([subName, { present, sessions }]) => ({
                    subject: subName,
                    attendancePercentage: calculateSubjectAttendancePercentage(present, sessions),
                    totalClasses: sessions,
                    attendedClasses: present,
                }),
            ),
        [subjectAttendance],
    );

    const marksChartData = useMemo(() => {
        return subjectMarks
            .filter((r) => r?.subName && r.marksObtained !== undefined && r.marksObtained !== null)
            .map((r) => ({
                ...r,
                subject: typeof r.subName === "object" ? r.subName.subName : String(r.subName),
            }));
    }, [subjectMarks]);

    const sclassName = userDetails?.sclassName;
    const classLabel =
        (typeof sclassName === "object" ? sclassName?.sclassName : null) ||
        (typeof sclassName === "string" ? sclassName : null) ||
        "—";
    const schoolRaw = userDetails?.school;
    const schoolLabel =
        (typeof schoolRaw === "object" ? schoolRaw?.schoolName : null) ||
        (typeof schoolRaw === "string" ? schoolRaw : null) ||
        "—";

    const studentRecordId = userDetails?._id || userDetails?.id;
    const enrollmentStatus = normalizeEnrollmentStatus(userDetails?.enrollmentStatus);
    const initial = (userDetails?.name || "?").charAt(0).toUpperCase();
    const photoSrc = useMemo(() => resolveUploadedMediaUrl(userDetails?.photoUrl), [userDetails?.photoUrl]);

    const formatUiDate = (value) => {
        if (value == null || value === "") return "—";
        if (typeof value === "object") return "—";
        const d = new Date(value);
        return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
    };

    const DetailsSection = () => (
        <Stack spacing={3}>
            <Paper
                elevation={0}
                sx={{
                    p: { xs: 2, sm: 3 },
                    borderRadius: 3,
                    border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                    background: (t) =>
                        `linear-gradient(135deg, ${alpha(t.palette.primary.main, 0.06)} 0%, ${alpha(t.palette.primary.light, 0.04)} 100%)`,
                }}
            >
                <Stack direction={{ xs: "column", sm: "row" }} spacing={3} alignItems={{ sm: "center" }}>
                    <Avatar
                        src={photoSrc}
                        alt={userDetails?.name}
                        sx={{
                            width: 72,
                            height: 72,
                            fontSize: "1.75rem",
                            fontWeight: 700,
                            bgcolor: "primary.main",
                        }}
                    >
                        {!photoSrc ? initial : null}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h5" fontWeight={800} gutterBottom>
                            {userDetails?.name}
                        </Typography>
                        <Stack direction="row" flexWrap="wrap" gap={1}>
                            <Chip icon={<Person sx={{ "&&": { fontSize: 18 } }} />} label={`Roll ${userDetails?.rollNum ?? "—"}`} variant="outlined" />
                            <Chip icon={<School sx={{ "&&": { fontSize: 18 } }} />} label={classLabel} color="primary" variant="outlined" />
                            <Chip label={schoolLabel} variant="outlined" />
                            {userDetails?.gender ? <Chip label={userDetails.gender} variant="outlined" /> : null}
                            <Chip label={enrollmentStatus} color={enrollmentChipColor[enrollmentStatus] || "default"} size="small" />
                        </Stack>
                        {subjectAttendance.length > 0 && (
                            <Box sx={{ maxWidth: 280, mt: 2 }}>
                                <CustomPieChart data={chartData} />
                            </Box>
                        )}
                    </Box>
                </Stack>
            </Paper>

            <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, border: (t) => `1px solid ${t.palette.divider}` }}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    Student record
                </Typography>
                <Stack spacing={2}>
                    <Stack direction="row" flexWrap="wrap" gap={1} useFlexGap>
                        <Chip icon={<CakeIcon sx={{ "&&": { fontSize: 18 } }} />} label={`DOB: ${formatUiDate(userDetails?.dateOfBirth)}`} variant="outlined" />
                        <Chip label={`Admitted: ${formatUiDate(userDetails?.admissionDate)}`} variant="outlined" />
                        {userDetails?.admissionStatus ? (
                            <Chip label={userDetails.admissionStatus} variant="outlined" />
                        ) : null}
                    </Stack>
                    <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight={700} letterSpacing={0.6}>
                            GUARDIAN / PARENT
                        </Typography>
                        <Stack spacing={0.75} sx={{ mt: 1 }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <GuardianIcon fontSize="small" color="action" />
                                <Typography variant="body2">{userDetails?.guardianName?.trim() || "—"}</Typography>
                            </Stack>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <PhoneIcon fontSize="small" color="action" />
                                <Typography variant="body2">{userDetails?.guardianPhone?.trim() || "—"}</Typography>
                            </Stack>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <EmailIcon fontSize="small" color="action" />
                                <Typography variant="body2">{userDetails?.guardianEmail?.trim() || "—"}</Typography>
                            </Stack>
                        </Stack>
                    </Box>
                </Stack>
            </Paper>

            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: (t) => `1px solid ${t.palette.divider}` }}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    Quick actions
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} flexWrap="wrap" useFlexGap>
                    <Button
                        variant="outlined"
                        startIcon={<FactCheck />}
                        onClick={() => navigate("/Teacher/attendance-entry")}
                        sx={{ borderRadius: 2 }}
                    >
                        Record attendance
                    </Button>
                    <PurpleButton variant="contained" onClick={() => navigate("/Teacher/marks-entry")}>
                        Submit marks
                    </PurpleButton>
                    {studentRecordId ? (
                        <Button
                            variant="outlined"
                            startIcon={<Grading />}
                            onClick={() => navigate(`/Teacher/report-card/${studentRecordId}`)}
                            sx={{ borderRadius: 2 }}
                        >
                            Report card
                        </Button>
                    ) : null}
                </Stack>
            </Paper>
        </Stack>
    );

    const AttendanceSection = () => (
        <Stack spacing={2}>
            <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ sm: "center" }} justifyContent="space-between" spacing={2}>
                <Typography variant="h6" fontWeight={700}>
                    Attendance
                </Typography>
                <ToggleButtonGroup
                    value={attendanceView}
                    exclusive
                    onChange={(_, v) => v && setAttendanceView(v)}
                    size="small"
                >
                    <ToggleButton value="table" sx={{ textTransform: "none", gap: 0.75 }}>
                        <TableRows fontSize="small" /> Table
                    </ToggleButton>
                    <ToggleButton value="chart" sx={{ textTransform: "none", gap: 0.75 }}>
                        <BarChartIcon fontSize="small" /> Chart
                    </ToggleButton>
                </ToggleButtonGroup>
            </Stack>

            {subjectAttendance.length > 0 ? (
                <>
                    {attendanceView === "table" && (
                        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: (t) => alpha(t.palette.primary.main, 0.06) }}>
                                        <TableCell sx={{ fontWeight: 700 }}>Subject</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Present</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Sessions</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>%</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                                            Details
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {Object.entries(groupAttendanceBySubject(subjectAttendance)).map(
                                        ([subName, { present, allData, subId, sessions }]) => {
                                            const pct = calculateSubjectAttendancePercentage(present, sessions);
                                            const isMine = mySubject && subName === mySubject;
                                            return (
                                                <React.Fragment key={subId}>
                                                    <TableRow hover sx={isMine ? { bgcolor: (t) => alpha(t.palette.primary.main, 0.04) } : undefined}>
                                                        <TableCell>
                                                            {subName}
                                                            {isMine ? (
                                                                <Chip label="Your subject" size="small" color="primary" sx={{ ml: 1 }} />
                                                            ) : null}
                                                        </TableCell>
                                                        <TableCell>{present}</TableCell>
                                                        <TableCell>{sessions}</TableCell>
                                                        <TableCell>{pct}%</TableCell>
                                                        <TableCell align="right">
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                endIcon={openStates[subId] ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                                                onClick={() => handleOpen(subId)}
                                                            >
                                                                Session log
                                                            </Button>
                                                        </TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableCell colSpan={5} sx={{ py: 0, border: 0 }}>
                                                            <Collapse in={openStates[subId]} timeout="auto" unmountOnExit>
                                                                <Box sx={{ py: 2, px: 1 }}>
                                                                    <Table size="small">
                                                                        <TableHead>
                                                                            <TableRow>
                                                                                <TableCell>Date</TableCell>
                                                                                <TableCell align="right">Status</TableCell>
                                                                            </TableRow>
                                                                        </TableHead>
                                                                        <TableBody>
                                                                            {allData.map((data, idx) => {
                                                                                const date = new Date(data.date);
                                                                                const dateString =
                                                                                    date.toString() !== "Invalid Date"
                                                                                        ? date.toISOString().substring(0, 10)
                                                                                        : "—";
                                                                                return (
                                                                                    <TableRow key={idx}>
                                                                                        <TableCell>{dateString}</TableCell>
                                                                                        <TableCell align="right">{data.status}</TableCell>
                                                                                    </TableRow>
                                                                                );
                                                                            })}
                                                                        </TableBody>
                                                                    </Table>
                                                                </Box>
                                                            </Collapse>
                                                        </TableCell>
                                                    </TableRow>
                                                </React.Fragment>
                                            );
                                        },
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                    {attendanceView === "chart" && (
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                            <CustomBarChart chartData={subjectData} dataKey="attendancePercentage" />
                        </Paper>
                    )}
                    <Typography variant="body2" color="text.secondary">
                        Overall attendance: <strong>{overallAttendancePercentage.toFixed(1)}%</strong>
                    </Typography>
                </>
            ) : (
                <Paper variant="outlined" sx={{ p: 4, borderRadius: 2, textAlign: "center" }}>
                    <Typography color="text.secondary">No attendance recorded yet.</Typography>
                </Paper>
            )}
        </Stack>
    );

    const MarksSection = () => (
        <Stack spacing={2}>
            <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ sm: "center" }} justifyContent="space-between" spacing={2}>
                <Typography variant="h6" fontWeight={700}>
                    Exam marks
                </Typography>
                <ToggleButtonGroup value={marksView} exclusive onChange={(_, v) => v && setMarksView(v)} size="small">
                    <ToggleButton value="table" sx={{ textTransform: "none", gap: 0.75 }}>
                        <TableRows fontSize="small" /> Table
                    </ToggleButton>
                    <ToggleButton value="chart" sx={{ textTransform: "none", gap: 0.75 }}>
                        <BarChartIcon fontSize="small" /> Chart
                    </ToggleButton>
                </ToggleButtonGroup>
            </Stack>

            {subjectMarks.length > 0 ? (
                <>
                    {marksView === "table" && (
                        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: (t) => alpha(t.palette.primary.main, 0.06) }}>
                                        <TableCell sx={{ fontWeight: 700 }}>Subject</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Marks</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {subjectMarks.map((result, index) => {
                                        if (!result?.subName || result.marksObtained === undefined || result.marksObtained === null) {
                                            return null;
                                        }
                                        const label =
                                            typeof result.subName === "object" ? result.subName.subName : String(result.subName);
                                        const isMine = mySubject && label === mySubject;
                                        return (
                                            <TableRow
                                                hover
                                                key={index}
                                                sx={isMine ? { bgcolor: (t) => alpha(t.palette.primary.main, 0.04) } : undefined}
                                            >
                                                <TableCell>
                                                    {label}
                                                    {isMine ? (
                                                        <Chip label="Your subject" size="small" color="primary" sx={{ ml: 1 }} />
                                                    ) : null}
                                                </TableCell>
                                                <TableCell>{result.marksObtained}</TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                    {marksView === "chart" && marksChartData.length > 0 && (
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                            <CustomBarChart chartData={marksChartData} dataKey="marksObtained" />
                        </Paper>
                    )}
                </>
            ) : (
                <Paper variant="outlined" sx={{ p: 4, borderRadius: 2, textAlign: "center" }}>
                    <Typography color="text.secondary">No marks recorded yet.</Typography>
                </Paper>
            )}
        </Stack>
    );

    return (
        <Box
            sx={{
                minHeight: "100%",
                pb: 4,
                background: (t) =>
                    t.palette.mode === "light"
                        ? `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.04)} 0%, ${t.palette.grey[50]} 22%)`
                        : alpha(t.palette.common.black, 0.15),
            }}
        >
            {loading ? (
                <Container maxWidth="lg" sx={{ py: 4 }}>
                    <Skeleton variant="rounded" height={120} sx={{ borderRadius: 3, mb: 2 }} />
                    <Skeleton variant="rounded" height={48} sx={{ borderRadius: 2, mb: 2 }} />
                    <Skeleton variant="rounded" height={240} sx={{ borderRadius: 2 }} />
                </Container>
            ) : error ? (
                <Container maxWidth="sm" sx={{ py: 4 }}>
                    <Paper sx={{ p: 3, borderRadius: 2 }}>
                        <Typography color="error" gutterBottom>
                            Could not load this student.
                        </Typography>
                        <Button startIcon={<ArrowBack />} onClick={() => navigate(backTo)} sx={{ mt: 1 }}>
                            Back to class list
                        </Button>
                    </Paper>
                </Container>
            ) : userDetails ? (
                <Fade in timeout={350}>
                    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3 } }}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                            <IconButton
                                onClick={() => navigate(backTo)}
                                aria-label="Back"
                                sx={{ bgcolor: (t) => alpha(t.palette.primary.main, 0.08) }}
                            >
                                <ArrowBack />
                            </IconButton>
                            <Typography variant="body2" color="text.secondary">
                                {hasHomeroomClass ? "My class" : "Class"}
                            </Typography>
                            <Typography variant="body2" color="text.disabled">
                                /
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                                {userDetails.name}
                            </Typography>
                        </Stack>

                        <TabContext value={tab}>
                            <Paper
                                elevation={0}
                                sx={{
                                    borderRadius: 3,
                                    mb: 2,
                                    border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                                    overflow: "hidden",
                                }}
                            >
                                <TabList
                                    onChange={(_, v) => setTab(v)}
                                    variant="scrollable"
                                    scrollButtons="auto"
                                    sx={{
                                        px: 1,
                                        pt: 1,
                                        "& .MuiTab-root": { textTransform: "none", fontWeight: 600, minHeight: 48 },
                                    }}
                                >
                                    <Tab label="Details" value="1" />
                                    <Tab label="Attendance" value="2" />
                                    <Tab label="Marks" value="3" />
                                    <Tab label="Special needs" value="4" />
                                </TabList>
                            </Paper>

                            <Paper
                                elevation={0}
                                sx={{
                                    p: { xs: 2, sm: 3 },
                                    borderRadius: 3,
                                    border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                                    minHeight: 280,
                                }}
                            >
                                <TabPanel value="1" sx={{ p: 0 }}>
                                    <DetailsSection />
                                </TabPanel>
                                <TabPanel value="2" sx={{ p: 0 }}>
                                    <AttendanceSection />
                                </TabPanel>
                                <TabPanel value="3" sx={{ p: 0 }}>
                                    <MarksSection />
                                </TabPanel>
                                <TabPanel value="4" sx={{ p: 0 }}>
                                    <SpecialNeedsSection
                                        studentId={studentID}
                                        student={userDetails}
                                        onUpdated={() => dispatch(getUserDetails(studentID, "Student"))}
                                    />
                                </TabPanel>
                            </Paper>
                        </TabContext>
                    </Container>
                </Fade>
            ) : (
                <Container sx={{ py: 4 }}>
                    <Typography gutterBottom>{typeof response === "string" ? response : "Student not found."}</Typography>
                    <Button startIcon={<ArrowBack />} onClick={() => navigate(backTo)}>
                        Back
                    </Button>
                </Container>
            )}
        </Box>
    );
};

export default TeacherViewStudent;
